import { useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import { useI18n } from "../../i18n/i18n";

interface ChartData {
  year: number;
  principal: number;
  balance: number;
}

interface CompoundInterestChartProps {
  data: ChartData[];
  currency: string;
}

const CompoundInterestChart = ({
  data,
  currency,
}: CompoundInterestChartProps) => {
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const chartInstance = useRef<Chart | null>(null);
  const { t } = useI18n();

  useEffect(() => {
    if (!chartRef.current) return;

    // Destroy previous chart instance if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext("2d");
    if (!ctx) return;

    const years = data.map(
      (item) => `${t("tools.compound.year")} ${item.year}`,
    );
    const principalValues = data.map((item) => item.principal);
    const balanceValues = data.map((item) => item.balance);
    const currentCurrency = currency; // Capture currency in closure

    chartInstance.current = new Chart(ctx, {
      type: "line",
      data: {
        labels: years,
        datasets: [
          {
            label: t("tools.compound.totalContributions"),
            data: principalValues,
            borderColor: "#6B7280",
            backgroundColor: "rgba(107, 114, 128, 0.2)",
            borderWidth: 2,
            tension: 0.2,
            fill: true,
          },
          {
            label: t("tools.compound.totalBalance"),
            data: balanceValues,
            borderColor: "#EA580C",
            backgroundColor: "rgba(234, 88, 12, 0.2)",
            borderWidth: 2,
            tension: 0.2,
            fill: true,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            mode: "index",
            intersect: false,
            callbacks: {
              label: function (context) {
                let label = context.dataset.label || "";
                if (label) {
                  label += ": ";
                }
                if (context.parsed.y !== null) {
                  label += new Intl.NumberFormat(
                    currency === "USD" ? "en-US" : "de-DE",
                    {
                      style: "currency",
                      currency: currency,
                    },
                  ).format(context.parsed.y);
                }
                return label;
              },
            },
          },
          legend: { position: "top" },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: function (value) {
                return (
                  (currentCurrency === "USD" ? "$" : "€") +
                  new Intl.NumberFormat().format(value as number)
                );
              },
            },
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data, t]);

  return (
    <div style={{ height: "250px" }}>
      <canvas ref={chartRef} />
    </div>
  );
};

export default CompoundInterestChart;
